<template>
  <KioskCenteredLayout :class="$style.discountPageWrapper">
    <KioskSpinner v-if="discountCodeIsUpdating"/>
    <KioskMessageTitle :class="$style.discountTitle"
                       title="Discount Code" />
    <div :class="$style.discount">
      <InputField :class="getStyle"
                  v-model="$v.text.$model"
                  type="text"
      />
      <p v-if="$v.text.$error"
         :class="$style.errorMessage">
        Please, enter discount code.
      </p>
      <div v-if="discountErrors.length">
        <p v-for="(error, index) in discountErrors"
           :key="index"
           :class="$style.errorMessage">
          {{ error }}
        </p>
      </div>
    </div>

    <div :class="$style.discountButtons">
      <BaseButton :class="$style.cancelBtn"
                  :disabled="discountCodeIsUpdating"
                  @click="cancel">
        <svg v-svg
             :class="$style.cancelIcon"
             symbol="remove-icon-kiosk" />
      </BaseButton>
      <BaseButton :class="$style.submitBtn"
                  :disabled="discountCodeIsUpdating"
                  @click="checkDiscount">
        <svg v-svg
             :class="$style.submitIcon"
             symbol="check-icon-kiosk" />
      </BaseButton>
    </div>

    <div :class="$style.discountBottom">
      <Keyboard :disabled="discountCodeIsUpdating"
                v-model="$v.text.$model"/>
      <KioskTryBlock />
    </div>
  </KioskCenteredLayout>
</template>

<script>
import Component from 'vue-class-component';
import Vue from 'vue';
import { required } from 'vuelidate/lib/validators';
import KioskCenteredLayout from '@/layouts/KioskCenteredLayout';
import KioskTryBlock from '@/components/KioskTryBlock';
import InputField from '@/components/InputField';
import BaseButton from '@/components/BaseButton';
import Keyboard from '@/apps/kiosk/components/Keyboard';
import KioskMessageTitle from '@/components/KioskMessageTitle';
import { Mutation, Action } from 'vuex-class';
import { CART_DISPLAY, CART_PREPARE_ORDER, CART_CALC_ORDER } from '@/apps/cart/store/types';
import KioskSpinner from './KioskSpinner';

/**
 * Provide page for entering discount code
 */
@Component({
  components: {
    KioskSpinner,
    KioskCenteredLayout,
    KioskTryBlock,
    InputField,
    BaseButton,
    KioskMessageTitle,
    Keyboard,
  },
  validations: {
    text: { required },
  },
})
export default class KioskDiscount extends Vue {
  @Mutation(CART_DISPLAY) cartDisplay;
  @Action(CART_PREPARE_ORDER) prepareOrder;
  @Action(CART_CALC_ORDER) calcOrder;

  text = '';
  discountErrors = [];
  discountCodeIsUpdating = false;

  get getStyle() {
    if (this.$v.text.$error) {
      return [ this.$style.discountField, this.$style.errorField ];
    }

    return this.$style.discountField;
  }

  cancel() {
    this.discountCodeIsUpdating = true;
    this.$router.push({ name: 'menu' });
    this.cartDisplay(true);
  }

  checkDiscount() {
    if (!this.text) {
      return;
    }

    this.updateDiscount(this.text.toUpperCase());
  }

  /**
   * Submit promo code. Update order.
   * @param {string} promoCode
   */
  async updateDiscount(promoCode) {
    try {
      this.discountCodeIsUpdating = true;
      await this.prepareOrder({ promoCode });
      await this.calcOrder({ promoCode });
      this.discountErrors = [];
      this.$router.push({ name: 'discount-apply' });
    } catch (error) {
      if (error.response && error.response.data) {
        const { data } = error.response;

        if (data.data.promotion_code) {
          this.discountErrors = data.data.promotion_code;
        }
      }
    } finally {
      this.discountCodeIsUpdating = false;
    }
  }
}
</script>

<style lang="scss" module>
@import '@/style/variables.scss';
$cancel-btn: #ba1d1d;

.discountBottom {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
}

.discountTitle {
  margin-bottom: 50px;
}

.discount {
  width: 608px;
}

.discountPageWrapper .discountTitle + .discount {
  .discountField > div > div::before {
    border-color: rgba(255, 255, 255, 0.25);
    border-width: 3px;
  }
}

.discountPageWrapper .discountTitle + .discount {
  .discountField > div > div::after {
    border-width: 3px;
  }
}

.discountPageWrapper .discountTitle + .discount {
  .discountField.errorField > div > div::before {
    border-color: $red-color;
  }
}

.errorMessage {
  color: $red-color;
  font-size: 24px;
  line-height: 29px;
  text-align: center;
  font-style: italic;
  margin-top: -10px;
}

.discountPageWrapper .discountTitle + .discount .discountField input {
  font-family: "Gotham", sans-serif;
  padding-bottom: 30px;
  max-height: none;
  color: $white;
  font-size: 56px;
  line-height: 67px;
  text-align: center;
  font-weight: 300;
  text-transform: uppercase;
}

.discountPageWrapper .discountTitle + .discount .discountField input:focus {
  color: red;
}

.discountButtons {
  margin-top: 50px;
  margin-bottom: 600px;
}

.discountPageWrapper .discountButtons .cancelBtn,
.discountPageWrapper .discountButtons .submitBtn {
  width: 97px;
  height: 97px;
  border-radius: 50%;
}

.cancelIcon,
.submitIcon {
  width: 97px;
  height: 97px;
}

.cancelBtn {
  margin-right: 79px;
  background-color: $cancel-btn;
}

.submitBtn {
  background-color: $green-color;
}
</style>



// WEBPACK FOOTER //
// src/apps/kiosk/components/KioskDiscount.vue